import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import config from 'config'
import { orderBy } from 'lodash'
import { insertMessagesInItems } from 'lib/messagesLib'

import useSnackbar from '../Snackbar/useSnackbar'

const COMMON_ERROR_FETCHDATA_LABEL = 'common.error.fetchData'
const CARS_TO_BUY_ENDPOINT = 'cars-to-buy'
const CARS_TO_BUY_BLIND_ENDPOINT = 'cars-to-buy-blind'
const BLIND_MINIMUM_REPRICE_REGEX = /^Blind offer must be at least\s+(\d+)/i

const useCarsToBuy = () => {
  const { t } = useTranslation()
  const { showSnackbarMessage } = useSnackbar()

  const translateBlindMinimumOfferMessage = useCallback(
    (message) => {
      if (!message || typeof message !== 'string') {
        return null
      }
      const match = message.match(BLIND_MINIMUM_REPRICE_REGEX)
      if (!match) {
        return null
      }
      const amount = Number(match[1])
      const formattedAmount = Number.isFinite(amount)
        ? new Intl.NumberFormat().format(amount)
        : match[1]
      return t('carsToBuy.blind.minimumOffer', { amount: formattedAmount })
    },
    [t]
  )

  const [state, setState] = useState({
    isFetching: false,
    isUpdating: false,
    cars: [],
    total: 0,
    error: null,
  })

  const setStartFetching = useCallback(() => {
    setState((s) => ({
      ...s,
      isFetching: true,
      isUpdating: false,
    }))
  }, [setState])

  const setStopFetching = useCallback(
    (error) => {
      setState((s) => ({
        ...s,
        isFetching: false,
        error: error,
      }))
    },
    [setState]
  )

  const setStartUpdating = useCallback(() => {
    setState((s) => ({
      ...s,
      isFetching: false,
      isUpdating: true,
    }))
  }, [setState])

  const setStopUpdating = useCallback(() => {
    setState((s) => ({
      ...s,
      isUpdating: false,
    }))
  }, [setState])

  const setSuccessFetching = useCallback(
    (data) => {
      setState((s) => ({
        ...s,
        isFetching: false,
        isUpdating: false,
        ...data,
        error: null,
      }))
    },
    [setState]
  )

  const fetchCarsToBuy = useCallback(
    async (sessionId, partnerId, isBlind = false) => {
      setStartFetching()
      try {
        const endpoint = isBlind
          ? CARS_TO_BUY_BLIND_ENDPOINT
          : CARS_TO_BUY_ENDPOINT
        const results = await axios.get(
          `${config.apiBaseUrl}sessions/${sessionId}/partners/${partnerId}/${endpoint}`
        )

        const cars = orderBy(results.data.body.items, ['plate'], ['asc'])

        const messagesResult = await axios.get(
          `${config.apiBaseUrl}sessions/${sessionId}/messages`
        )

        const messages = messagesResult.data.body.messages.filter(
          (message) => message.partnerId === partnerId
        )

        insertMessagesInItems(messages, cars)

        setSuccessFetching({
          cars,
          total: results.data.body.total,
        })
      } catch (e) {
        let message = t(COMMON_ERROR_FETCHDATA_LABEL, {
          attribute: e.response?.status,
        })

        setStopFetching(e)

        showSnackbarMessage({
          severity: 'error',
          message,
        })
      }
    },
    [
      setStartFetching,
      setStopFetching,
      setSuccessFetching,
      showSnackbarMessage,
      t,
    ]
  )

  const updateChoice = useCallback(
    async (sessionId, partnerId, plate, choice, isBlind = false, reprice) => {
      setStartUpdating()

      try {
        const endpoint = isBlind
          ? CARS_TO_BUY_BLIND_ENDPOINT
          : CARS_TO_BUY_ENDPOINT
        const payload = { choice }
        if (reprice !== undefined) {
          payload.reprice = reprice
        }
        const result = await axios.post(
          `${config.apiBaseUrl}sessions/${sessionId}/partners/${partnerId}/${endpoint}/${plate}`,
          payload
        )
        const {
          data: { body: response },
        } = result
        if (isBlind && response?.success === false) {
          const message =
            response?.messageString &&
            typeof response.messageString === 'string'
              ? t(response.messageString)
              : response?.message ||
                t(COMMON_ERROR_FETCHDATA_LABEL, {
                  attribute: result?.status,
                })

          showSnackbarMessage({
            severity: 'error',
            message,
          })
        }
        setStopUpdating()
        await fetchCarsToBuy(sessionId, partnerId, isBlind)
        return response.success
      } catch (e) {
        const backendMessage = e.response?.data?.message
        const translatedMessage =
          translateBlindMinimumOfferMessage(backendMessage)
        const message =
          translatedMessage ||
          backendMessage ||
          t(COMMON_ERROR_FETCHDATA_LABEL, {
            attribute: e.response?.status,
          })

        setStopUpdating()

        showSnackbarMessage({
          severity: 'error',
          message,
        })
        return null
      }
    },
    [
      fetchCarsToBuy,
      setStartUpdating,
      setStopUpdating,
      showSnackbarMessage,
      t,
      translateBlindMinimumOfferMessage,
    ]
  )

  const setReprice = useCallback(
    async (sessionId, partnerId, plate, reprice, isBlind = false) => {
      if (isBlind) {
        const hasReprice =
          reprice !== null && reprice !== undefined && reprice !== ''
        const choice = hasReprice ? 'first' : 'none'
        await updateChoice(sessionId, partnerId, plate, choice, true, reprice)
        return
      }

      setStartUpdating()

      try {
        await axios.post(
          `${config.apiBaseUrl}sessions/${sessionId}/partners/${partnerId}/${CARS_TO_BUY_ENDPOINT}/${plate}/reprice`,
          { reprice }
        )
        setStopUpdating()
        await fetchCarsToBuy(sessionId, partnerId, isBlind)
      } catch (e) {
        const backendMessage = e.response?.data?.message
        const translatedMessage =
          translateBlindMinimumOfferMessage(backendMessage)
        const message =
          translatedMessage ||
          backendMessage ||
          t(COMMON_ERROR_FETCHDATA_LABEL, {
            attribute: e.response?.status,
          })

        setStopUpdating()

        showSnackbarMessage({
          severity: 'error',
          message,
        })
      }
    },
    [
      fetchCarsToBuy,
      setStartUpdating,
      setStopUpdating,
      showSnackbarMessage,
      t,
      translateBlindMinimumOfferMessage,
      updateChoice,
    ]
  )

  const setBlindPrice = useCallback(
    async (sessionId, partnerId, plate, reprice) => {
      setStartUpdating()

      try {
        await axios.post(
          `${config.apiBaseUrl}sessions/${sessionId}/partners/${partnerId}/${CARS_TO_BUY_BLIND_ENDPOINT}/${plate}/reprice`,
          { reprice }
        )
        setStopUpdating()
        await fetchCarsToBuy(sessionId, partnerId, true)
      } catch (e) {
        const rawMessage = e.response?.data?.message
        const message =
          rawMessage &&
          typeof rawMessage === 'string' &&
          rawMessage.includes('.')
            ? t(rawMessage)
            : rawMessage ||
              t(COMMON_ERROR_FETCHDATA_LABEL, {
                attribute: e.response?.status,
              })

        setStopUpdating()

        showSnackbarMessage({
          severity: 'error',
          message,
        })
      }
    },
    [fetchCarsToBuy, setStartUpdating, setStopUpdating, showSnackbarMessage, t]
  )

  const addMessage = useCallback(
    async (sessionId, partnerId, plate, message, isBlind = false) => {
      setStartUpdating()

      try {
        await axios.post(
          `${config.apiBaseUrl}sessions/${sessionId}/partners/${partnerId}/messages/${plate}`,
          { message }
        )
        setStopUpdating()
        await fetchCarsToBuy(sessionId, partnerId, isBlind)
      } catch (e) {
        const message =
          e.response?.data?.message ||
          t(COMMON_ERROR_FETCHDATA_LABEL, {
            attribute: e.response?.status,
          })

        setStopUpdating()

        showSnackbarMessage({
          severity: 'error',
          message,
        })
      }
    },
    [fetchCarsToBuy, setStartUpdating, setStopUpdating, showSnackbarMessage, t]
  )

  const toggleFavorite = useCallback(
    async (sessionId, partnerId, plate, currentFavoriteState) => {
      const newFavoriteState = !currentFavoriteState

      // Optimistic update
      setState((s) => ({
        ...s,
        cars: s.cars.map((car) =>
          car.plate === plate ? { ...car, favorite: newFavoriteState } : car
        ),
      }))

      try {
        await axios.patch(
          `${config.apiBaseUrl}sessions/${sessionId}/partners/${partnerId}/favorites`,
          { plate, favorite: newFavoriteState }
        )
      } catch (e) {
        // Revert on error
        setState((s) => ({
          ...s,
          cars: s.cars.map((car) =>
            car.plate === plate
              ? { ...car, favorite: currentFavoriteState }
              : car
          ),
        }))

        const message =
          e.response?.data?.message ||
          t(COMMON_ERROR_FETCHDATA_LABEL, {
            attribute: e.response?.status,
          })

        showSnackbarMessage({
          severity: 'error',
          message,
        })
      }
    },
    [showSnackbarMessage, t]
  )

  return {
    cars: state.cars,
    total: state.total,
    isFetching: state.isFetching,
    isUpdating: state.isUpdating,
    error: state.error,
    fetchCarsToBuy,
    updateChoice,
    setReprice,
    setBlindPrice,
    addMessage,
    toggleFavorite,
  }
}

export default useCarsToBuy
